import { defaultPaginatedResponse, mapPaginatedResponse } from '@/mappers/common.mapper';
import {
  mapFilePreviewUrlResponse,
  mapKnowledgeBaseCreateResponse,
  mapKnowledgeBaseDetailResponse,
  mapKnowledgeBaseResponse,
  mapKnowledgeBaseSyncStatusResponse,
} from '@/mappers/knowledge-base.mapper';

import { DEFAULT_PAGE_SIZE } from '@/constants';

import apiClient from '@/lib/api-client';
import { raiseAxiosErrorOrDefault } from '@/lib/error';

import type { PaginatedResponse, PaginationParams } from '@/types/common.type';
import type {
  DeleteKnowledgeBaseResponse,
  KnowledgeBase,
  KnowledgeBaseCreateRequest,
  KnowledgeBaseDetail,
  KnowledgeBaseSyncStatus,
  KnowledgeBaseUpdateRequest,
} from '@/types/knowledge.type';

/**
 * Fetch all knowledge bases with pagination
 */
export const fetchAllKnowledgeBases = async (
  params?: PaginationParams
): Promise<PaginatedResponse<KnowledgeBase>> => {
  try {
    return await apiClient.get<PaginatedResponse<KnowledgeBase>>(
      '/knowledge-bases',
      mapPaginatedResponse(mapKnowledgeBaseResponse),
      {
        params: {
          page: params?.page || 1,
          page_size: params?.page_size || DEFAULT_PAGE_SIZE,
          ...(params?.query && { query: params.query }),
        },
      }
    );
  } catch {
    return defaultPaginatedResponse<KnowledgeBase>();
  }
};

/**
 * Fetch a single knowledge base by ID
 */
export const fetchKnowledgeBaseById = async (kbId: number): Promise<KnowledgeBaseDetail> => {
  try {
    const response = await apiClient.get<KnowledgeBaseDetail>(
      `/knowledge-base/${kbId}`,
      mapKnowledgeBaseDetailResponse
    );
    return response;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to fetch knowledge base');
  }
};

/**
 * Create a new knowledge base
 */
export const createKnowledgeBase = async (
  payload: KnowledgeBaseCreateRequest
): Promise<KnowledgeBase> => {
  try {
    const response = await apiClient.post<KnowledgeBase>(
      '/knowledge-bases',
      payload,
      mapKnowledgeBaseCreateResponse
    );
    return response;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to create knowledge base');
  }
};

/**
 * Update a knowledge base
 */
export const updateKnowledgeBase = async (
  kbId: number,
  payload: KnowledgeBaseUpdateRequest
): Promise<KnowledgeBaseDetail> => {
  try {
    const response = await apiClient.put<KnowledgeBaseDetail>(
      `/knowledge-base/${kbId}`,
      payload,
      mapKnowledgeBaseDetailResponse
    );
    return response;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to update knowledge base');
  }
};

/**
 * Delete a knowledge base
 */
export const deleteKnowledgeBase = async (kbId: number): Promise<DeleteKnowledgeBaseResponse> => {
  try {
    const response = await apiClient.getInstance().delete(`/knowledge-base/${kbId}`);

    return {
      accepted: response.status === 202,
      status: response.status,
    };
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to delete knowledge base');
  }
};

/**
 * Upload a document to a knowledge base
 */
export const uploadDocument = async (kbId: number, file: File): Promise<void> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    await apiClient.post(`/knowledge-bases/${kbId}/upload`, formData, undefined, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to upload document');
  }
};

/**
 * Delete a document from a knowledge base
 */
export const deleteDocument = async (kbId: number, filePath: string): Promise<void> => {
  try {
    await apiClient.delete(`/knowledge-bases/${kbId}/documents/${encodeURIComponent(filePath)}`);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to delete document');
  }
};

/**
 * Sync a knowledge base with its data source
 */
export const syncKnowledgeBase = async (kbId: number): Promise<void> => {
  try {
    await apiClient.post(`/knowledge-bases/${kbId}/sync`);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to sync knowledge base');
  }
};

/**
 * Check the ingestion status of a knowledge base
 */
export const checkIngestionStatus = async (kbId: number): Promise<KnowledgeBaseSyncStatus> => {
  try {
    const response = await apiClient.get<KnowledgeBaseSyncStatus>(
      `/knowledge-bases/${kbId}/ingestion-status`,
      mapKnowledgeBaseSyncStatusResponse
    );
    return response;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to check ingestion status');
  }
};

/**
 * Get a preview URL for a file in the knowledge base
 */
export const getFilePreviewUrl = async (filePath: string): Promise<string> => {
  try {
    const response = await apiClient.get<string>(
      `/knowledge-bases/file-preview/${encodeURIComponent(filePath)}`,
      mapFilePreviewUrlResponse
    );
    return response;
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to get file preview URL');
  }
};
